import React, { useEffect, useState } from "react";
import { Modal } from "antd";
import Select from "react-select";
import {
  IMPORT_MODES,
  IMPORT_MODE_LABELS,
  versionFilters,
  emptyVersionPanel,
  MEASUREMENTS,
  PROTOCOLS,
} from "helpers/estimatingDatabase/databaseRows";

// ImportOptionInputViewModel: how an imported sheet meets the rows already
// on the page.
export const ImportModeModal = ({ open, onCancel, onConfirm }) => {
  const [mode, setMode] = useState(IMPORT_MODES.ADD_NEW_OVERWRITE_EXISTING);
  return (
    <Modal open={open} title="Import options" onCancel={onCancel} onOk={() => onConfirm(mode)} okText="Import">
      <div className="d-flex flex-column gap-2" data-testid="import-mode-modal">
        {Object.values(IMPORT_MODES).map((m) => (
          <label key={m} className="d-flex align-items-center gap-2 mb-0 f-14 cursor-pointer">
            <input type="radio" name="import-mode" checked={mode === m} onChange={() => setMode(m)} />
            {IMPORT_MODE_LABELS[m]}
          </label>
        ))}
        <div className="text-muted f-12">
          Nothing is written until you save.
        </div>
      </div>
    </Modal>
  );
};

// ExportDataToOtherVersionViewModel: pick a company (BidLight admins only)
// and one of its versions; only the ticked rows are copied there.
export const SaveToVersionModal = ({ open, workspace, onCancel, onConfirm }) => {
  const { isAdmin, companies, selectedCompany, orgId, loadTemplates, template } = workspace;
  const [company, setCompany] = useState(null);
  const [versions, setVersions] = useState([]);
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setCompany(selectedCompany || null);
      setVersion(null);
    }
  }, [open, selectedCompany]);

  const targetOrg = (company && company.value) || orgId;

  useEffect(() => {
    let cancelled = false;
    if (!open || !targetOrg) return undefined;
    setLoading(true);
    loadTemplates(targetOrg)
      .then((list) => !cancelled && setVersions(list))
      .catch(() => !cancelled && setVersions([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, targetOrg, loadTemplates]);

  const versionOptions = versions.map((v, i) => ({ value: i, label: v.Code }));

  return (
    <Modal
      open={open}
      title="Save to other version"
      onCancel={onCancel}
      okButtonProps={{ disabled: !version }}
      onOk={() =>
        onConfirm({
          targetOrgId: targetOrg,
          targetTemplate: version,
          targetFilters: versionFilters(version),
          sameAsCurrent:
            targetOrg === orgId && version && template && version.Code === template.Code,
        })
      }
      okText="Save"
    >
      <div className="d-flex flex-column gap-3" data-testid="save-version-modal">
        {isAdmin && (
          <div>
            <label className="f-14 mb-1">Company</label>
            <Select
              options={companies}
              value={company}
              isClearable
              placeholder="BidLight"
              onChange={(c) => {
                setCompany(c);
                setVersion(null);
              }}
            />
          </div>
        )}
        <div>
          <label className="f-14 mb-1">Version</label>
          <Select
            options={versionOptions}
            isLoading={loading}
            value={version ? versionOptions.find((o) => versions[o.value] === version) : null}
            onChange={(o) => setVersion(o ? versions[o.value] : null)}
          />
        </div>
        <div className="text-muted f-12">
          Only the ticked rows are copied. Rows with the same code in the target version are
          overwritten.
        </div>
      </div>
    </Modal>
  );
};

export const confirmDialog = ({ title, content, okText = "Continue", onOk }) =>
  Modal.confirm({ title, content, okText, cancelText: "Cancel", onOk });

// "Which item do you want to add?" (DatabaseDialogViewModel with two
// buttons on the desktop).
export const ChoiceModal = ({ open, title, choices, onChoose, onCancel }) => (
  <Modal open={open} title={title} onCancel={onCancel} footer={null}>
    <div className="d-flex gap-2" data-testid="choice-modal">
      {(choices || []).map((c) => (
        <button key={c.value} type="button" className="btn bg-bid text-white" onClick={() => onChoose(c.value)}>
          {c.label}
        </button>
      ))}
    </div>
  </Modal>
);

// The desktop's "add version" panel (DatabaseTabPage, EnableAddNewVersion):
// a version code plus the free filters - country, state, city, labor type,
// project type, currency, measurements and protocol. Load creates the
// version and loads it (DatabaseTabViewModel.AddNewVersion).
const opt = (value, label) => ({ value, label: label || value });

export const NewVersionModal = ({ open, workspace, onCancel, onCreate }) => {
  const { countries, currencies, projectTypes, laborTypes } = workspace;
  const [panel, setPanel] = useState(emptyVersionPanel());
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setPanel(emptyVersionPanel());
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const country = (countries || []).find((c) => c.iso3 === panel.Country) || null;
  const state = country ? (country.states || []).find((s) => s.state_code === panel.State) || null : null;
  const set = (patch) => setPanel((p) => ({ ...p, ...patch }));
  const select = (label, options, value, onChange, clearable = true) => (
    <div>
      <label className="f-14 mb-1">{label}</label>
      <Select
        aria-label={label}
        classNamePrefix={`new-version-${label.replace(/\s+/g, "-").toLowerCase()}`}
        options={options}
        value={options.find((o) => o.value === value) || null}
        isClearable={clearable}
        onChange={(o) => onChange(o ? o.value : null)}
      />
    </div>
  );

  const submit = async () => {
    setBusy(true);
    const problem = await onCreate(panel);
    setBusy(false);
    if (problem) setError(problem);
  };

  return (
    <Modal
      open={open}
      title="New version"
      onCancel={onCancel}
      onOk={submit}
      okText="Load"
      okButtonProps={{ loading: busy }}
      destroyOnClose
    >
      <div className="d-flex flex-column gap-2" data-testid="new-version-modal">
        <div>
          <label className="f-14 mb-1">Version code</label>
          <input
            className="form-control"
            aria-label="Version code"
            value={panel.VersionCode}
            onChange={(e) => set({ VersionCode: e.target.value })}
          />
        </div>
        {select(
          "Country",
          (countries || []).map((c) => opt(c.iso3, c.name)),
          panel.Country,
          // Country setter: a new country clears state and city.
          (v) => set({ Country: v, State: null, City: null }),
          false
        )}
        {select(
          "State",
          ((country && country.states) || []).map((s) => opt(s.state_code, s.name)),
          panel.State,
          (v) => set({ State: v, City: null })
        )}
        {select(
          "City",
          ((state && state.cities) || []).map((c) => opt(c.name)),
          panel.City,
          (v) => set({ City: v })
        )}
        {select(
          "Labor type",
          (laborTypes || []).filter((t) => t.APIUrlIdentifier).map((t) => opt(t.APIUrlIdentifier, t.Category)),
          panel.LaborType,
          (v) => set({ LaborType: v })
        )}
        {select(
          "Project type",
          (projectTypes || []).filter((t) => t.APIUrlIdentifier).map((t) => opt(t.APIUrlIdentifier, t.Category)),
          panel.ProjectType,
          (v) => set({ ProjectType: v })
        )}
        {select("Currency", (currencies || []).map((c) => opt(c)), panel.Currency, (v) => set({ Currency: v }), false)}
        {select("Measurements", MEASUREMENTS.map((m) => opt(m)), panel.Measurements, (v) => set({ Measurements: v || "Imperial" }), false)}
        {select("Protocol", PROTOCOLS.map((p) => opt(p)), panel.Protocol, (v) => set({ Protocol: v || "RevitUniFormat" }), false)}
        {error && (
          <div className="text-danger f-12" data-testid="new-version-error">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
};
